import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Tag } from 'lucide-react'
import { Button } from '../ui/button'
import { addListingTag } from '@/lib/api'

interface ListingTagButtonProps {
    symbol: string
    hours?: number
    variant?: 'default' | 'outline' | 'ghost' | 'secondary'
    size?: 'default' | 'sm' | 'lg' | 'icon'
    className?: string
    showLabel?: boolean
    onSuccess?: () => void
}

/**
 * Button to add a temporary "Listing" tag to a symbol.
 * The tag auto-expires after the specified hours (default 24h).
 */
export function ListingTagButton({
    symbol,
    hours = 24,
    variant = 'outline',
    size = 'sm',
    className,
    showLabel = true,
    onSuccess,
}: ListingTagButtonProps) {
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: () => addListingTag(symbol, hours),
        onSuccess: (data) => {
            if (data.ok) {
                // Invalidate tags queries to refresh UI
                queryClient.invalidateQueries({ queryKey: ['tags'] })
                onSuccess?.()
            }
        },
    })

    return (
        <Button
            variant={variant}
            size={size}
            className={className}
            onClick={(e) => {
                e.stopPropagation()
                mutation.mutate()
            }}
            disabled={mutation.isPending}
            title={`Add temporary "Listing" tag (expires in ${hours}h)`}
        >
            <Tag className="h-3 w-3" />
            {showLabel && (
                <span className="ml-1">{mutation.isPending ? 'Adding...' : 'Listing'}</span>
            )}
        </Button>
    )
}
